import React from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { Check, TrendingUp, DollarSign, Heart, Clock, Package, Users, BarChart3, ChefHat, Eye } from "lucide-react";

const StopAuxInvendus = () => {
  // Schema.org Service
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Stop aux invendus en boulangerie",
    "description": "Réduisez les invendus et récupérez de la marge : analyse, plan d'action 7–30 jours, rituel hebdo 15 min. Intervention Annecy, Savoie/Haute-Savoie.",
    "provider": {
      "@type": "Person",
      "name": "Christophe Marrama",
      "email": "christophe.marrama@croissanceconsulting.net",
      "telephone": "+33674245563"
    },
    "areaServed": {
      "@type": "Place",
      "name": "Annecy, Savoie, Haute-Savoie, France"
    }
  };

  const objectives = [
    {
      icon: TrendingUp,
      title: "Moins d'invendus",
      description: "Pain, viennoiserie, snacking, pâtisserie"
    },
    {
      icon: DollarSign,
      title: "Plus de trésorerie",
      description: "Moins de pertes = cash immédiat"
    },
    {
      icon: Heart,
      title: "Plus de sérénité",
      description: "Production ajustée, décisions simples"
    }
  ];

  const whyItHappens = [
    "Production pilotée \"à l'habitude\" (pas aux ventes réelles)",
    "Gamme trop large / produits chronophages",
    "Mauvais calibrage des grammages, fiches, pertes cachées",
    "Ruptures puis surproduction \"pour compenser\"",
    "Manque de rituels de suivi (10–15 min/semaine)"
  ];

  const whatICheck = [
    {
      icon: Clock,
      title: "Volumes & horaires",
      description: "Pics, flux, coups de feu"
    },
    {
      icon: BarChart3,
      title: "Gamme & mix-produit",
      description: "80/20, produits aimants vs boulets"
    },
    {
      icon: ChefHat,
      title: "Organisation production",
      description: "Goulots, temps, préparations"
    },
    {
      icon: Package,
      title: "Stocks & pertes",
      description: "Retours, casse, DLC, surstocks"
    },
    {
      icon: Eye,
      title: "Vitrine & vente",
      description: "Mise en avant marge, écoulement"
    },
    {
      icon: DollarSign,
      title: "Chiffres rapides",
      description: "Achats, prix, marge \"repères\", invendus"
    }
  ];

  const deliverables = [
    "Synthèse 1 page : constats + priorités",
    "Plan d'action 7–30 jours (checklist)",
    "Rituel hebdo 15 min : 4 indicateurs + 3 questions",
    "Quick wins invendus : production / gamme / écoulement / vente"
  ];

  const quickWins = [
    "Ajuster 3 références \"gros volumes\" avec un pilotage simple",
    "Mettre 1 règle d'écoulement en boutique (timing + mise en avant)",
    "Réduire 1 produit chronophage qui ne paie pas",
    "Standardiser 2 fiches / grammages qui dérivent"
  ];

  const faqs = [
    {
      question: "Combien de temps avant de voir une baisse des invendus ?",
      answer: "Souvent dès la première semaine si on suit 2–3 actions simples + le rituel hebdo."
    },
    {
      question: "Est-ce que tu travailles aussi le snacking ?",
      answer: "Oui, si c'est une source de pertes ou d'écarts de marge."
    },
    {
      question: "Est-ce adapté aux petites équipes ?",
      answer: "Oui. Justement : méthode simple, indicateurs courts, décisions rapides."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Stop aux invendus en boulangerie | Croissance 🥐consulting</title>
        <meta name="description" content="Réduisez les invendus et récupérez de la marge : analyse, plan d'action 7–30 jours, rituel hebdo 15 min. Intervention Annecy, Savoie/Haute-Savoie." />
        <link rel="canonical" href="https://croissanceconsulting.net/stop-aux-invendus" />
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#FBF7EF]">
        {/* Hero Section */}
        <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#4A3A2A] font-poppins text-center mb-8">
              Stop aux invendus : récupérer de la marge sans travailler plus
            </h1>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
                Les invendus ne sont pas "normaux". Ils mangent ta marge, ton énergie et ta trésorerie.
              </p>
              <p className="text-lg text-[#2D2D2D] leading-relaxed">
                Je t'aide à mesurer, comprendre et réduire les pertes avec une méthode simple : terrain + chiffres.
              </p>
            </div>
          </div>
        </section>

        {/* Ce qu'on vise */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Ce qu'on vise
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {objectives.map((obj, index) => {
                const Icon = obj.icon;
                return (
                  <div key={index} className="bg-[#FBF7EF] rounded-xl p-6 text-center">
                    <div className="w-12 h-12 bg-[#B65C3A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-[#B65C3A]" size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-[#4A3A2A] font-poppins mb-2">
                      {obj.title}
                    </h3>
                    <p className="text-[#2D2D2D]">
                      {obj.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pourquoi ça arrive */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Pourquoi ça arrive (souvent)
            </h2>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <ul className="space-y-4">
                {whyItHappens.map((reason, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#B65C3A]/10 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-[#B65C3A] font-bold text-sm">{index + 1}</span>
                    </div>
                    <span className="text-[#2D2D2D]">{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Ce que je regarde sur place */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Ce que je regarde sur place
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whatICheck.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-[#FBF7EF] rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 bg-[#6B7A3A]/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-[#6B7A3A]" size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-[#4A3A2A] font-poppins mb-2">
                      {item.title}
                    </h3>
                    <p className="text-[#2D2D2D] text-sm">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Le déroulé */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Le déroulé (simple)
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#B65C3A] rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-2">
                    Immersion terrain (2–3h)
                  </h3>
                  <p className="text-[#2D2D2D]">
                    Observation + échanges
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#6B7A3A] rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-2">
                    Analyse rapide
                  </h3>
                  <p className="text-[#2D2D2D]">
                    Repères invendus / marge / points de fuite
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#B65C3A] rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-2">
                    Synthèse (48h)
                  </h3>
                  <p className="text-[#2D2D2D]">
                    3–5 actions prioritaires + plan 7–30 jours + rituel hebdo 15 min
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ce que tu reçois */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Ce que tu reçois
            </h2>
            <div className="bg-[#FBF7EF] rounded-2xl p-8 shadow-xl">
              <ul className="space-y-4">
                {deliverables.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="text-[#6B7A3A] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#2D2D2D]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Exemples de quick wins */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Exemples de quick wins
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {quickWins.map((win, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#B65C3A]/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="text-[#B65C3A]" size={18} />
                    </div>
                    <p className="text-[#2D2D2D]">{win}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-3xl text-center">
            <div className="bg-[#6B7A3A]/5 rounded-2xl p-10 shadow-xl">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins mb-4">
                Prêt à réduire tes invendus ?
              </h2>
              <p className="text-lg text-[#2D2D2D] mb-8">
                On fait un point 15 minutes et je te dis si tu as un levier rapide.
              </p>
              <Link to="/contact">
                <Button className="bg-[#B65C3A] hover:bg-[#9d4e2f] text-white font-medium px-10 py-6 text-lg">
                  Proposer un échange
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              FAQ
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="text-lg font-bold text-[#4A3A2A] font-poppins mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-[#2D2D2D] leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Zone */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="bg-[#FBF7EF] rounded-xl p-6 inline-block">
              <p className="text-lg text-[#2D2D2D] font-medium">
                <span className="font-bold text-[#4A3A2A]">Zone :</span> Annecy • Savoie/Haute-Savoie (et + selon déplacement)
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default StopAuxInvendus;
